import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { Category, Product } from "@/lib/products";
import { dbError } from "./db";

// Corpo dos handlers extraído em funções simples: createServerFn lança "No
// Start context found" quando chamada fora de uma requisição real (inclusive
// sem middleware/validator), então testar a const exportada diretamente
// quebra — mesmo motivo documentado em addresses.functions.ts.

export async function fetchActiveProducts(): Promise<Product[]> {
  const { createPublicSupabaseClient } = await import("@/lib/supabase-public.server");
  const { data, error } = await createPublicSupabaseClient()
    .from("products")
    .select("*, category:categories(id,name,slug,description,sort_order,parent_id)")
    .eq("is_active", true)
    .order("is_featured", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw dbError(error, "src/lib/products.functions.ts");
  return (data ?? []) as unknown as Product[];
}

export async function fetchActiveCategories(): Promise<Category[]> {
  const { createPublicSupabaseClient } = await import("@/lib/supabase-public.server");
  const { data, error } = await createPublicSupabaseClient()
    .from("categories")
    .select("id,name,slug,description,sort_order,parent_id")
    .eq("is_active", true)
    .order("sort_order");
  if (error) throw dbError(error, "src/lib/products.functions.ts");
  return (data ?? []) as Category[];
}

export async function searchActiveProducts(query: string): Promise<Product[]> {
  const { createPublicSupabaseClient } = await import("@/lib/supabase-public.server");
  const { data, error } = await createPublicSupabaseClient()
    .from("products")
    .select("*, category:categories(id,name,slug,description,sort_order,parent_id)")
    .eq("is_active", true)
    .textSearch("search_vector", query, { type: "websearch", config: "portuguese_unaccent" })
    .order("is_featured", { ascending: false })
    .limit(100);
  if (error) throw dbError(error, "src/lib/products.functions.ts");
  return (data ?? []) as unknown as Product[];
}

export async function fetchActiveProductBySlug(slug: string): Promise<Product | null> {
  const { createPublicSupabaseClient } = await import("@/lib/supabase-public.server");
  const { data, error } = await createPublicSupabaseClient()
    .from("products")
    .select("*, category:categories(id,name,slug,description,sort_order,parent_id)")
    .eq("slug", slug)
    .eq("is_active", true)
    .maybeSingle();
  if (error) throw dbError(error, "src/lib/products.functions.ts");
  return (data ?? null) as unknown as Product | null;
}

export const getProducts = createServerFn({ method: "GET" }).handler(() => fetchActiveProducts());

export const getCategories = createServerFn({ method: "GET" }).handler(() =>
  fetchActiveCategories(),
);

export const searchProducts = createServerFn({ method: "GET" })
  .validator((query: string) => z.string().min(1).max(200).parse(query))
  .handler(async ({ data: query }) => searchActiveProducts(query));

export const getProductBySlug = createServerFn({ method: "GET" })
  .validator((slug: string) => z.string().min(1).parse(slug))
  .handler(async ({ data: slug }) => fetchActiveProductBySlug(slug));
